/**
 * Background sync: debounced, sequential queue, version check, retries.
 * Mount exactly once app-wide (menu screen).
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import AsyncStorage from '@react-native-async-storage/async-storage';
import moment from 'moment';
import { useAuthStore } from '../../../shared/auth/store';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';
import type { DayConfig, SubscriptionDataResponse } from '../subscription/types';
import { deferCalendarRefreshAfterMenuSave } from '../../query/menuCalendarRefreshDefer';
import { subscriptionKeys } from '../subscription/keys';
import { useMenuStore } from './store';
import { patchMenuQueryCacheFromStore } from './cache';
import { useMenuBooking } from './bookingHooks';

const PENDING_MEAL_BACKUP_PREFIX = 'pending_meal_selection_';

function resolveCustomerSubscriptionId(
  query_client: ReturnType<typeof useQueryClient>,
  customer_id: number,
  date_ascii: string,
  day_config: DayConfig | null | undefined,
): number | undefined {
  const from_config = day_config?.selectedSubscriptionID;
  if (typeof from_config === 'number' && Number.isFinite(from_config) && from_config > 0) {
    return from_config;
  }
  const data = query_client.getQueryData(subscriptionKeys.detail(customer_id)) as SubscriptionDataResponse | undefined;
  const subs = data?.subscriptions ?? [];
  const m = moment(date_ascii, 'YYYY-MM-DD', true);
  if (!m.isValid()) {
    return undefined;
  }
  for (const sub of subs) {
    const start = moment(sub.start_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
    const end = moment(sub.end_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
    if (!start.isValid() || !end.isValid()) {
      continue;
    }
    if (m.isBetween(start, end, 'day', '[]')) {
      const id = Number(sub.id);
      if (Number.isFinite(id) && id > 0) {
        return id;
      }
    }
  }
  return undefined;
}

const SYNC_DEBOUNCE_MS = 0;
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

export interface UseMenuSyncOptions {
  onSyncSuccess?: (date: string, isDoubleBox: boolean) => void;
  manualOnly?: boolean;
}

export function useMenuSync(options: UseMenuSyncOptions = {}) {
  const { onSyncSuccess, manualOnly = false } = options;
  const customer_id = useAuthStore(s => s.user?.id);
  const query_client = useQueryClient();
  const { mutateAsync: bookMenu } = useMenuBooking();

  const book_menu_ref = useRef(bookMenu);
  book_menu_ref.current = bookMenu;

  const is_syncing_ref = useRef(false);
  const is_mounted_ref = useRef(true);
  const debounce_ref = useRef<ReturnType<typeof setTimeout> | null>(null);
  const on_sync_error_ref = useRef<((date: string, message: string) => void) | null>(null);
  const on_sync_success_ref = useRef(onSyncSuccess);
  on_sync_success_ref.current = onSyncSuccess;

  const [is_sync_in_progress, set_is_sync_in_progress] = useState(false);
  const dirty_count = useMenuStore(s => s.dirtyDates.size);
  const dirty_sync_generation = useMenuStore(s => {
    if (s.dirtyDates.size === 0) {
      return 0;
    }
    let acc = 0;
    for (const d of s.dirtyDates) {
      acc += s.syncVersionByDate[d] ?? 0;
    }
    return acc;
  });

  useEffect(() => {
    is_mounted_ref.current = true;
    return () => {
      is_mounted_ref.current = false;
    };
  }, []);

  const setOnSyncError = useCallback((fn: (date: string, message: string) => void) => {
    on_sync_error_ref.current = fn;
  }, []);

  const process_next_sync = useCallback(async () => {
    if (is_syncing_ref.current || typeof customer_id !== 'number') {
      return;
    }

    const dates = useMenuStore.getState().getDirtyDates();
    if (dates.length === 0) {
      if (is_mounted_ref.current) {set_is_sync_in_progress(false);}
      return;
    }

    is_syncing_ref.current = true;
    if (is_mounted_ref.current) {set_is_sync_in_progress(true);}

    const date_to_sync = dates[0];
    if (!date_to_sync || date_to_sync.length < 10) {
      useMenuStore.getState().clearDateDirty(date_to_sync);
      is_syncing_ref.current = false;
      setTimeout(() => process_next_sync(), 0);
      return;
    }
    const slot = useMenuStore.getState().getSlot(date_to_sync);
    const day_config = slot?.dayConfig;

    if (!day_config) {
      useMenuStore.getState().clearDateDirty(date_to_sync);
      is_syncing_ref.current = false;
      setTimeout(() => process_next_sync(), 0);
      return;
    }

    const version_at_send = useMenuStore.getState().getSyncVersion(date_to_sync);
    const payload = useMenuStore.getState().prepareBookingData(date_to_sync);

    const subscription_id = resolveCustomerSubscriptionId(query_client, customer_id, date_to_sync, day_config);

    let attempt = 0;
    const try_send = async (params: {
      customer_id: number;
      customer_subscription_id: number;
      date: string;
      remove_box_index: number;
      menu_items: typeof payload;
      is_double_box: boolean;
    }): Promise<void> => {
      try {
        const response = await book_menu_ref.current(params);

        if (response.code === 200 || response.code === 201) {
          useMenuStore.getState().confirmSyncSuccess(date_to_sync, version_at_send);
          if (typeof customer_id === 'number') {
            await patchMenuQueryCacheFromStore(query_client, customer_id, date_to_sync, params.is_double_box);
            deferCalendarRefreshAfterMenuSave(customer_id, date_to_sync);
          }
          if (params.is_double_box) {
            on_sync_success_ref.current?.(date_to_sync, true);
          }
        } else {
          throw new Error(response?.message ?? 'Changes not saved');
        }
      } catch (err: unknown) {
        attempt += 1;
        if (attempt < MAX_RETRIES) {
          await new Promise<void>(resolve => setTimeout(() => resolve(), RETRY_DELAY_MS));
          return try_send(params);
        }

        const message = err instanceof Error ? err.message : 'Connection error';
        on_sync_error_ref.current?.(date_to_sync, message);
        useMenuStore.getState().rollbackDate(date_to_sync);
      }
    };

    let requeue_after_this_pass = true;
    try {
      if (subscription_id == null) {
        requeue_after_this_pass = false;
        on_sync_error_ref.current?.(date_to_sync, 'Could not save: missing subscription for this date.');
      } else {
        const params = {
          customer_id,
          customer_subscription_id: subscription_id,
          date: date_to_sync,
          remove_box_index: 0,
          menu_items: payload,
          is_double_box: slot?.isBox2Data ?? false,
        };

        const box_key = slot?.isBox2Data ? 'box2' : 'box1';
        const backup_key = `${PENDING_MEAL_BACKUP_PREFIX}${date_to_sync}_${box_key}`;
        const backup_value = {
          date: date_to_sync,
          customer_id,
          customer_subscription_id: subscription_id,
          is_double_box: slot?.isBox2Data ?? false,
          menu_items: payload,
          timestamp: Date.now(),
        };
        AsyncStorage.setItem(backup_key, JSON.stringify(backup_value)).catch(() => {});

        await try_send(params);
      }
    } finally {
      is_syncing_ref.current = false;
      const remaining = useMenuStore.getState().getDirtyDates();
      if (remaining.length > 0 && requeue_after_this_pass) {
        setTimeout(() => process_next_sync(), 0);
      } else if (is_mounted_ref.current) {
        set_is_sync_in_progress(false);
      }
    }
  }, [customer_id, query_client]);

  useEffect(() => {
    if (manualOnly) {
      if (debounce_ref.current) {
        clearTimeout(debounce_ref.current);
        debounce_ref.current = null;
      }
      return;
    }

    if (dirty_count === 0) {
      if (debounce_ref.current) {
        clearTimeout(debounce_ref.current);
        debounce_ref.current = null;
      }
      return;
    }

    if (debounce_ref.current) {
      clearTimeout(debounce_ref.current);
    }

    debounce_ref.current = setTimeout(() => {
      debounce_ref.current = null;
      process_next_sync();
    }, SYNC_DEBOUNCE_MS);

    return () => {
      if (debounce_ref.current) {clearTimeout(debounce_ref.current);}
    };
  }, [dirty_count, dirty_sync_generation, process_next_sync, manualOnly]);

  const flushSyncForDate = useCallback(
    async (date: string): Promise<void> => {
      if (debounce_ref.current) {
        clearTimeout(debounce_ref.current);
        debounce_ref.current = null;
      }
      const key = ensureAsciiDateString(date) || date;
      const matches_flush_date = (dates: string[]) => dates.some(d => (ensureAsciiDateString(d) || d) === key);
      if (!matches_flush_date(useMenuStore.getState().getDirtyDates())) {
        return;
      }
      for (let guard = 0; guard < 32; guard += 1) {
        const st = useMenuStore.getState();
        if (!matches_flush_date(st.getDirtyDates())) {
          return;
        }
        const before_sig = st.getDirtyDates().join('\u0000');
        await process_next_sync();
        const st2 = useMenuStore.getState();
        if (!matches_flush_date(st2.getDirtyDates())) {
          return;
        }
        if (st2.getDirtyDates().join('\u0000') === before_sig) {
          return;
        }
      }
    },
    [process_next_sync],
  );

  return {
    setOnSyncError,
    isRequestSyncing: is_sync_in_progress,
    isSyncing: is_sync_in_progress || dirty_count > 0,
    hasPendingChanges: dirty_count > 0,
    flushSyncForDate,
  };
}
